/**
 * The server refused this session for an institute reason.
 *
 * Two codes, both from tenancy/tenant_access.py:
 *
 *   TENANT_HOST_FORBIDDEN   on an institute's own domain, this account is not
 *                           one of its people
 *   INSTITUTE_UNAVAILABLE   master has suspended or closed the institute, or
 *                           switched its portal off
 *
 * The server says whether to end the session (`sign_out`). It is false when
 * someone in two institutes asked for the blocked one; they keep working in
 * the other, and the page that made the call shows its error as usual.
 *
 * The sign-in endpoints answer the same way, but those live under /public/
 * and the Login page shows that message inline, so they are left alone here.
 *
 * Like sessionEviction, this does NOT call Signout: the account itself is
 * fine, and another institute or domain may still accept it.
 *
 * Registered once, at app bootstrap (see App.tsx).
 */
import axios from 'axios';

import { clearLocalSession } from '@/services/sessionEviction';

export const ACCESS_REFUSED_QUERY = 'signedOut=access-refused';

/** Key the refusal message is parked under for the Login page to show. */
export const ACCESS_REFUSED_MESSAGE_KEY = 'tenantAccessRefusedMessage';

const LOGIN_PATH = '/login';
const CODES = new Set(['TENANT_HOST_FORBIDDEN', 'INSTITUTE_UNAVAILABLE']);

const refusalBody = (error: unknown): Record<string, unknown> | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return null;
  const body = (error.response.data ?? {}) as Record<string, unknown>;
  return CODES.has(String(body.code)) ? body : null;
};

let handled = false;

const endSession = (body: Record<string, unknown>): void => {
  if (handled) return;
  handled = true;

  clearLocalSession();

  try {
    if (typeof body.message === 'string') {
      sessionStorage.setItem(ACCESS_REFUSED_MESSAGE_KEY, body.message);
    }
  } catch {
    // Storage unavailable: Login falls back to a generic sentence.
  }

  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(LOGIN_PATH)) {
    handled = false;
    return;
  }
  window.location.replace(`${LOGIN_PATH}?${ACCESS_REFUSED_QUERY}`);
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const body = refusalBody(error);
    const url = axios.isAxiosError(error) ? String(error.config?.url ?? '') : '';
    // Older servers did not send sign_out; their only refusal was the
    // tenant-host one, which always ended the session.
    const signOut = body ? body.sign_out !== false : false;
    if (body && signOut && !url.includes('/public/')) endSession(body);
    return Promise.reject(error);
  },
);
